/**
 * Lịch sự kiện khách hàng — trạng thái trang + nạp dữ liệu.
 *
 * Tháng và trang nằm trên URL để link chia sẻ được ("gửi anh lịch tháng 5").
 */
import { useCallback, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import type { FilterItemConfig } from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getCustomerCalendarEvents } from '@/modules/Customer/services'
import { getEmployeesByIds } from '@/modules/Employee/services'
import { getCustomerEventColumns } from '../utils/customer-events-config'
import {
    createCsFollowersFilter,
    createSaleFollowersFilter,
} from '../utils/customer-list-shared-config'
import usePermissionsStore from '@/stores/usePermissionsStore'

const DEFAULT_PAGE_SIZE = 20
const PAGE_SIZE_OPTIONS = [20, 50, 100] as const
const MAX_PAGE = Math.floor(2_147_483_647 / 100) + 1

interface CustomerEventFilters {
    month: number
    sale_follower_ids: number[]
    cs_follower_ids: number[]
}

/** Kẹp về khoảng hợp lệ; URL là input của người dùng nên ?month=99 phải ra tháng hiện tại. */
const parseMonth = (raw: string | null): number => {
    const parsed = Number(raw)
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > 12) {
        return new Date().getMonth() + 1
    }
    return parsed
}

const parsePage = (raw: string | null): number => {
    const parsed = Number(raw)
    return Number.isSafeInteger(parsed) && parsed > 0 && parsed <= MAX_PAGE
        ? parsed
        : 1
}

const parsePageSize = (raw: string | null): number => {
    const parsed = Number(raw)
    return PAGE_SIZE_OPTIONS.includes(
        parsed as (typeof PAGE_SIZE_OPTIONS)[number]
    )
        ? parsed
        : DEFAULT_PAGE_SIZE
}

const parseEmployeeIds = (searchParams: URLSearchParams, key: string) =>
    Array.from(
        new Set(
            searchParams
                .getAll(key)
                .map(Number)
                .filter((id) => Number.isSafeInteger(id) && id > 0)
        )
    ).sort((a, b) => a - b)

export const useCustomerEvents = () => {
    const { t } = useTranslation('customer')
    const [searchParams, setSearchParams] = useSearchParams()
    const canFilterCustomerEventFollowers = usePermissionsStore(
        (state) => state.canFilterCustomerEventFollowers
    )

    const month = parseMonth(searchParams.get('month'))
    const page = parsePage(searchParams.get('page'))
    const pageSize = parsePageSize(searchParams.get('page_size'))
    const saleFollowerIds = useMemo(
        () =>
            canFilterCustomerEventFollowers
                ? parseEmployeeIds(searchParams, 'sale_follower_ids')
                : [],
        [canFilterCustomerEventFollowers, searchParams]
    )
    const csFollowerIds = useMemo(
        () =>
            canFilterCustomerEventFollowers
                ? parseEmployeeIds(searchParams, 'cs_follower_ids')
                : [],
        [canFilterCustomerEventFollowers, searchParams]
    )
    const { control: filterControl } = useForm<CustomerEventFilters>({
        defaultValues: {
            month: new Date().getMonth() + 1,
            sale_follower_ids: [],
            cs_follower_ids: [],
        },
        values: {
            month,
            sale_follower_ids: saleFollowerIds,
            cs_follower_ids: csFollowerIds,
        },
    })

    useEffect(() => {
        if (canFilterCustomerEventFollowers) return
        if (
            !searchParams.has('sale_follower_ids') &&
            !searchParams.has('cs_follower_ids')
        ) {
            return
        }
        setSearchParams(
            (current) => {
                const next = new URLSearchParams(current)
                next.delete('sale_follower_ids')
                next.delete('cs_follower_ids')
                return next
            },
            { replace: true }
        )
    }, [canFilterCustomerEventFollowers, searchParams, setSearchParams])

    const setParams = useCallback(
        (next: Record<string, number>) => {
            setSearchParams(
                (current) => {
                    const merged = new URLSearchParams(current)
                    Object.entries(next).forEach(([key, value]) =>
                        merged.set(key, String(value))
                    )
                    return merged
                },
                { replace: true }
            )
        },
        [setSearchParams]
    )

    // Đổi tháng hoặc cỡ trang phải về trang 1: đang ở trang 3 mà tháng mới chỉ có 1 trang
    // thì bảng sẽ trắng dù có dữ liệu.
    const handleMonthChange = useCallback(
        (nextMonth: number) => setParams({ month: nextMonth, page: 1 }),
        [setParams]
    )
    const handlePageChange = useCallback(
        (nextPage: number) => setParams({ page: nextPage }),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (nextSize: number) => setParams({ page_size: nextSize, page: 1 }),
        [setParams]
    )
    const handleFollowerChange = useCallback(
        (key: 'sale_follower_ids' | 'cs_follower_ids', ids: number[]) => {
            const normalizedIds = Array.from(
                new Set(
                    ids
                        .map(Number)
                        .filter((id) => Number.isSafeInteger(id) && id > 0)
                )
            ).sort((a, b) => a - b)
            setSearchParams(
                (current) => {
                    const next = new URLSearchParams(current)
                    next.delete(key)
                    normalizedIds.forEach((id) => next.append(key, String(id)))
                    next.set('page', '1')
                    return next
                },
                { replace: true }
            )
        },
        [setSearchParams]
    )
    const filterItems = useMemo<FilterItemConfig[]>(() => {
        const monthFilter: FilterItemConfig = {
            name: 'month',
            type: 'select',
            label: t('calendar_events.filter_label'),
            placeholder: t('calendar_events.filter_label'),
            options: Array.from({ length: 12 }, (_, index) => ({
                label: t('calendar_events.month_label', { month: index + 1 }),
                value: index + 1,
            })),
            onChange: (value: number) => handleMonthChange(Number(value)),
        }

        if (!canFilterCustomerEventFollowers) return [monthFilter]

        const saleFilter = createSaleFollowersFilter(t, {
            label: t('calendar_events.sale_followers'),
        })
        const csFilter = createCsFollowersFilter(t, {
            label: t('calendar_events.cs_followers'),
            includeSpecialOptions: false,
        })

        return [
            monthFilter,
            {
                ...saleFilter,
                name: 'sale_follower_ids',
                onChange: (ids: number[]) =>
                    handleFollowerChange('sale_follower_ids', ids),
            },
            {
                ...csFilter,
                name: 'cs_follower_ids',
                onChange: (ids: number[]) =>
                    handleFollowerChange('cs_follower_ids', ids),
            },
        ]
    }, [
        canFilterCustomerEventFollowers,
        handleFollowerChange,
        handleMonthChange,
        t,
    ])

    const { data, renderState, isFetching } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.CUSTOMER_CALENDAR_EVENTS,
            month,
            page,
            pageSize,
            saleFollowerIds,
            csFollowerIds,
        ],
        queryFn: () =>
            getCustomerCalendarEvents({
                month,
                page,
                page_size: pageSize,
                ...(canFilterCustomerEventFollowers && {
                    sale_follower_ids: saleFollowerIds,
                    cs_follower_ids: csFollowerIds,
                }),
            }),
        placeholderData: true,
        refetchInterval: 5 * 60 * 1000,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: true,
        refetchIntervalInBackground: false,
    })

    const events = useMemo(() => data?.results ?? [], [data])
    const pagination = data?.pagination

    // Backend chỉ trả employee_id. Gom id của đúng trang đang xem rồi hỏi tên một lần —
    // rẻ hơn nhiều so với nạp toàn bộ nhân viên, và react-query cache theo danh sách id.
    const followerIds = useMemo(() => {
        const ids = new Set<number>()
        events.forEach((event) => event.followers?.forEach((id) => ids.add(id)))
        return Array.from(ids).sort((a, b) => a - b)
    }, [events])

    const { data: employees } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.EMPLOYEES, 'by-ids', followerIds],
            queryFn: () => getEmployeesByIds({ employee_ids: followerIds }),
            enabled: followerIds.length > 0,
        },
        // Tên người theo dõi hỏng thì cột đó lùi về "#<id>"; đừng vì thế mà che cả bảng
        // bằng khung loading hay trang lỗi.
        { showLoading: false, showError: false }
    )

    const employeeNames = useMemo(() => {
        const map: Record<number, string> = {}
        employees?.forEach((employee) => {
            if (employee?.employee_id) {
                map[employee.employee_id] = employee.employee_name
            }
        })
        return map
    }, [employees])

    const columns = useMemo(
        () => getCustomerEventColumns(t, employeeNames),
        [t, employeeNames]
    )

    return {
        t,
        month,
        page,
        pageSize,
        canFilterCustomerEventFollowers,
        filterControl,
        filterItems,
        events,
        total: pagination?.total ?? 0,
        columns,
        stateComponent: renderState(),
        isFetching,
        handleMonthChange,
        handlePageChange,
        handlePageSizeChange,
    }
}
